import { TNullable } from '@/types';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useInterval } from '@/hooks/useInterval';
import { WARNING_TIME_THRESHOLD } from '../constants';
import { useAppointmentsStore } from '@/stores/appointments';
import { useKioskStore } from '@/stores/kiosk';

/**
 * This hook polls for the nearest kiosk appointment and manages the warning state for the kiosk.
 * It uses a web worker to handle the warning timer, ensuring that the UI remains responsive.
 * The hook checks for new appointments every minute and updates the warning state accordingly.
 * @returns Return time.
 */
export const useKioskSessionOverride = () => {
  const [time, setTime] = useState<TNullable<number>>(null);
  const { kioskAppointments, setBlockDashboardButtonsSession } = useKioskStore(
    (state) => ({
      kioskAppointments: state.kioskAppointments,
      setBlockDashboardButtonsSession: state.setBlockDashboardButtonsSession,
    }),
  );
  const appointments = useAppointmentsStore((state) => state.appointments);

  // this useEffect controls timer display
  // null - hide timer
  // 0 - display 0
  // int value - seconds to countdown
  useEffect(() => {
    // don't have appointments - don't show timer
    if (!kioskAppointments.length) {
      setTime(null);
      return;
    }
    // have 1 closest appointment
    let nearestAppointment = kioskAppointments[0];
    // is this appointment mine?
    if (
      nearestAppointment?.id &&
      appointments.find((item) => item.id === nearestAppointment?.id)
    ) {
      // do you have 2nd closest appointment?
      if (kioskAppointments.length > 1) {
        // analyze next appointment if exists
        nearestAppointment = kioskAppointments[1];
      } else {
        // show nothing if next appointment doesn't exist
        setTime(null);
        return;
      }
    }
    if (nearestAppointment?.start) {
      const initialValue = -dayjs().diff(dayjs(nearestAppointment.start), 's');
      if (initialValue <= WARNING_TIME_THRESHOLD && initialValue >= 0) {
        // appointment in 15 mins or less
        setTime(initialValue);
        return;
      } else if (initialValue <= 0) {
        // appointment in progress
        setTime(0);
        return;
      }
      // appointment in more than 15 mins
    }
    setTime(null);
  }, [appointments, kioskAppointments]);

  // this useEffect controls disabling dashboard buttons if appointment in action
  // or it starts in 15 mins
  useEffect(() => {
    // don't have appointments - don't block buttons
    if (kioskAppointments.length === 0) {
      setBlockDashboardButtonsSession(false);
      return;
    }
    // if closest appointment in 15 mins or in progress - block dashboard buttons
    const initialValue = -dayjs().diff(dayjs(kioskAppointments[0].start), 's');
    setBlockDashboardButtonsSession(initialValue <= WARNING_TIME_THRESHOLD);
  }, [kioskAppointments, setBlockDashboardButtonsSession]);

  useInterval(() => {
    if (time === null || time <= 0) return;
    setTime(time - 1);
  }, 1000);

  return time;
};
